/**
 * The Probe area's interview list, shared by its sidebar and its pages.
 *
 * One read per organization, kept while the area is open, so the sidebar
 * and the page beside it never disagree or ask twice. A finished interview
 * refreshes it (`refreshProbeInterviews`, from the call's save), so the new
 * rehearsal is in the sidebar by the time the debrief is written.
 *
 * @module components/apps/probe/probeInterviewsStore
 */
import { useCallback, useEffect } from "react";
import { create } from "zustand";

import {
  listProbeInterviews,
  PROBE_INTERVIEW_LIST_LIMIT,
  type ProbeInterviewSummary,
} from "../../../state/probeHistory";

export type ProbeInterviewsStatus = "loading" | "ready" | "error";

interface OrgEntry {
  readonly status: ProbeInterviewsStatus;
  /** The last list read, kept through a refresh or a failed retry. */
  readonly interviews: ReadonlyArray<ProbeInterviewSummary>;
  /** Bumped per read, so an older answer never overwrites a newer one. */
  readonly request: number;
}

interface ProbeInterviewsState {
  readonly byOrg: Readonly<Record<string, OrgEntry>>;
}

const useProbeInterviewsState = create<ProbeInterviewsState>(() => ({ byOrg: {} }));

const EMPTY: ReadonlyArray<ProbeInterviewSummary> = [];

/** Read (or read again) one organization's interviews. */
export function refreshProbeInterviews(organizationId: string): void {
  const previous = useProbeInterviewsState.getState().byOrg[organizationId];
  const request = (previous?.request ?? 0) + 1;
  useProbeInterviewsState.setState((state) => ({
    byOrg: {
      ...state.byOrg,
      [organizationId]: {
        // A refresh with a list already on screen keeps showing it.
        status: previous?.status === "ready" ? "ready" : "loading",
        interviews: previous?.interviews ?? EMPTY,
        request,
      },
    },
  }));
  void listProbeInterviews(organizationId, PROBE_INTERVIEW_LIST_LIMIT).then((interviews) => {
    const current = useProbeInterviewsState.getState().byOrg[organizationId];
    if (current?.request !== request) return;
    useProbeInterviewsState.setState((state) => ({
      byOrg: {
        ...state.byOrg,
        [organizationId]:
          interviews === null
            ? { ...current, status: current.interviews.length > 0 ? "ready" : "error" }
            : { status: "ready", interviews, request },
      },
    }));
  });
}

/**
 * Read the list again when it holds this interview's review in another
 * state than the page just read, so the sidebar names it by the review's
 * title once the review lands.
 */
export function syncProbeInterview(
  organizationId: string,
  interview: Pick<ProbeInterviewSummary, "id" | "review">,
): void {
  const entry = useProbeInterviewsState.getState().byOrg[organizationId];
  const listed = entry?.interviews.find((row) => row.id === interview.id);
  if (listed !== undefined && listed.review?.status !== interview.review?.status) {
    refreshProbeInterviews(organizationId);
  }
}

export interface ProbeInterviewsView {
  readonly status: ProbeInterviewsStatus;
  readonly interviews: ReadonlyArray<ProbeInterviewSummary>;
  readonly reload: () => void;
}

/** The organization's interviews, newest first; read on first use. */
export function useProbeInterviews(organizationId: string | null): ProbeInterviewsView {
  const entry = useProbeInterviewsState((state) =>
    organizationId === null ? undefined : state.byOrg[organizationId],
  );
  useEffect(() => {
    if (organizationId === null) return;
    if (useProbeInterviewsState.getState().byOrg[organizationId] === undefined) {
      refreshProbeInterviews(organizationId);
    }
  }, [organizationId]);
  const reload = useCallback(() => {
    if (organizationId !== null) refreshProbeInterviews(organizationId);
  }, [organizationId]);
  return {
    status: entry?.status ?? "loading",
    interviews: entry?.interviews ?? EMPTY,
    reload,
  };
}
